import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errText } from "@/lib/api";
import { scoreColor } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Award, Clock, ShieldAlert, Maximize, AlertTriangle } from "lucide-react";

const shuffled = (n) => {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export default function QuizTake() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [qOrder, setQOrder] = useState([]);      // display pos -> original question index
  const [optOrder, setOptOrder] = useState([]);  // [origQ] -> (display pos -> original option index)
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [started, setStarted] = useState(false);
  const [warnOverlay, setWarnOverlay] = useState(false);

  // --- anti-cheat refs ---
  const violationRef = useRef(0);          // focus violations driving auto-submit
  const eventsRef = useRef([]);            // all logged events {type, at}
  const lastViolationTs = useRef(0);
  const startedRef = useRef(false);
  const resultRef = useRef(false);

  useEffect(() => {
    api.get(`/quizzes/${id}`)
      .then((r) => {
        setQuiz(r.data);
        const qs = r.data.questions;
        setAnswers(Array.from({ length: qs.length }, () => []));
        setQOrder(shuffled(qs.length));
        setOptOrder(qs.map((q) => shuffled(q.options.length)));
        setTimeLeft((r.data.duration_minutes || 10) * 60);
      })
      .catch((e) => { toast.error(errText(e)); navigate("/kuis"); });
    // eslint-disable-next-line
  }, [id]);

  const select = (origQ, origOi) => {
    const multiple = quiz.questions[origQ].multiple;
    setAnswers((prev) => {
      const next = [...prev];
      const cur = next[origQ] || [];
      if (multiple) next[origQ] = cur.includes(origOi) ? cur.filter((x) => x !== origOi) : [...cur, origOi];
      else next[origQ] = cur.includes(origOi) ? [] : [origOi];
      return next;
    });
  };

  const exitFullscreen = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) { /* noop */ }
  };

  const submit = useCallback(async (opts = {}) => {
    const { auto = false, byViolation = false } = opts;
    if (submitting || resultRef.current) return;
    if (!auto && answers.some((a) => !a || a.length === 0)) {
      if (!window.confirm("Masih ada soal yang belum dijawab. Kirim sekarang?")) return;
    }
    setSubmitting(true);
    try {
      const payload = answers.map((a) => a || []);
      const { data } = await api.post(`/quizzes/${id}/submit`, {
        answers: payload,
        violation_count: violationRef.current,
        auto_submitted: byViolation,
        violation_events: eventsRef.current,
      });
      resultRef.current = true;
      startedRef.current = false;
      setWarnOverlay(false);
      setResult(data);
      await exitFullscreen();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSubmitting(false);
    }
  }, [answers, id, submitting]);

  const submitRef = useRef();
  submitRef.current = submit;

  // --- record a focus violation (tab switch / blur / exit fullscreen) ---
  const recordFocusViolation = useCallback((type) => {
    if (!startedRef.current || resultRef.current) return;
    const now = Date.now();
    if (now - lastViolationTs.current < 1200) return; // de-dupe one physical action
    lastViolationTs.current = now;
    violationRef.current += 1;
    eventsRef.current.push({ type, at: new Date().toISOString() });
    if (violationRef.current >= 2) {
      toast.error("Pelanggaran kedua terdeteksi. Jawaban dikumpulkan otomatis.");
      submitRef.current?.({ auto: true, byViolation: true });
    } else {
      setWarnOverlay(true);
    }
  }, []);

  // --- soft event (right-click/copy/printscreen/devtools): log + warn, no auto-submit ---
  const softEvent = useCallback((type, msg) => {
    if (!startedRef.current || resultRef.current) return;
    eventsRef.current.push({ type, at: new Date().toISOString() });
    if (msg) toast.warning(msg);
  }, []);

  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
    } catch (e) { /* fullscreen may be unavailable; continue */ }
  };

  const beginQuiz = async () => {
    await enterFullscreen();
    startedRef.current = true;
    setStarted(true);
  };

  const resumeFromWarning = async () => {
    setWarnOverlay(false);
    await enterFullscreen();
  };

  // --- attach anti-cheat listeners once started ---
  useEffect(() => {
    if (!started) return;

    const onVisibility = () => { if (document.hidden) recordFocusViolation("pindah_tab"); };
    const onBlur = () => recordFocusViolation("keluar_jendela");
    const onFsChange = () => { if (!document.fullscreenElement) recordFocusViolation("keluar_fullscreen"); };
    const onContext = (e) => { e.preventDefault(); softEvent("klik_kanan", "Klik kanan dinonaktifkan selama kuis."); };
    const onCopyCut = (e) => { e.preventDefault(); softEvent("salin_teks", "Menyalin teks tidak diizinkan."); };
    const onPaste = (e) => { e.preventDefault(); softEvent("tempel_teks", "Menempel teks tidak diizinkan."); };
    const onKeyDown = (e) => {
      // block common capture / devtools / copy shortcuts
      const k = (e.key || "").toLowerCase();
      if (k === "printscreen") {
        try { navigator.clipboard?.writeText(""); } catch (_) { /* noop */ }
        softEvent("print_screen", "Tangkapan layar terdeteksi & clipboard dikosongkan.");
      }
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(k)) || (e.ctrlKey && k === "u")) {
        e.preventDefault();
        softEvent("devtools_shortcut", "Alat pengembang dinonaktifkan selama kuis.");
      }
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "p", "s"].includes(k)) {
        e.preventDefault();
      }
    };

    // devtools open detector (size heuristic) — soft warn once
    let devtoolsWarned = false;
    const devInterval = setInterval(() => {
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      if ((wDiff > 180 || hDiff > 200) && !devtoolsWarned) {
        devtoolsWarned = true;
        softEvent("devtools_terbuka", "Alat pengembang terdeteksi. Harap tutup.");
      }
      if (wDiff <= 180 && hDiff <= 200) devtoolsWarned = false;
    }, 1500);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("contextmenu", onContext);
    document.addEventListener("copy", onCopyCut);
    document.addEventListener("cut", onCopyCut);
    document.addEventListener("paste", onPaste);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearInterval(devInterval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("copy", onCopyCut);
      document.removeEventListener("cut", onCopyCut);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [started, recordFocusViolation, softEvent]);

  // timer only runs after quiz started
  useEffect(() => {
    if (!started || timeLeft === null || result) return;
    if (timeLeft <= 0) {
      toast.warning("Waktu habis! Jawaban dikirim otomatis.");
      submitRef.current?.({ auto: true });
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, result, started]);

  if (!quiz || qOrder.length === 0) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  // ---------- RESULT ----------
  if (result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-up">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50"><Award className="h-8 w-8 text-amber-500" /></div>
          <h2 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Hasil Kuis</h2>
          <p className="text-slate-500">{quiz.title}</p>
          <p data-testid="quiz-score-display" className={`mt-4 font-heading text-6xl font-extrabold ${scoreColor(result.score)}`}>{result.score}</p>
          <p className="text-sm text-slate-400">dari skala 0–10</p>
          <p className="mt-2 text-slate-600">Benar <span className="font-bold text-slate-900">{result.correct_count}</span> dari {result.total} soal</p>
          {violationRef.current > 0 && (
            <p data-testid="quiz-violation-note" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
              <ShieldAlert className="h-3.5 w-3.5" /> Tercatat {violationRef.current} pelanggaran selama kuis
            </p>
          )}
          <div className="mt-6 flex justify-center gap-3">
            <button data-testid="quiz-back-button" onClick={() => navigate("/kuis")} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Kembali ke Daftar Kuis</button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-heading font-bold text-slate-900">Pembahasan Jawaban</h3>
          {result.results.map((r, i) => {
            const sel = r.selected || [];
            const cor = r.correct_indices || [];
            const credit = r.credit != null ? r.credit : (sel.length === cor.length && cor.every((x) => sel.includes(x)) ? 1 : 0);
            const full = credit >= 1;
            const none = credit <= 0;
            return (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="font-semibold text-slate-800">{i + 1}. {r.question}{cor.length > 1 && <span className="ml-2 text-xs font-normal text-slate-400">(jawaban ganda)</span>}</p>
                <div className="mt-3 space-y-2">
                  {r.options.map((opt, oi) => {
                    const isCorrect = cor.includes(oi);
                    const isSelected = sel.includes(oi);
                    return (
                      <div key={oi} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${
                        isCorrect ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                        isSelected ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-100 text-slate-600"
                      }`}>
                        {isCorrect ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : isSelected ? <XCircle className="h-4 w-4 text-rose-500" /> : <span className="h-4 w-4" />}
                        {opt}
                      </div>
                    );
                  })}
                </div>
                <p data-testid={`quiz-result-credit-${i}`} className={`mt-2 text-xs font-semibold ${full ? "text-emerald-600" : none ? "text-rose-600" : "text-amber-600"}`}>
                  {full ? "Jawaban benar" : none ? "Jawaban salah" : `Benar sebagian — poin ${Math.round(credit * 100)}%`}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------- INSTRUCTIONS (before start) ----------
  if (!started) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-up">
        <button onClick={() => navigate("/kuis")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-3"><ChevronLeft className="h-4 w-4" /> Batal</button>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="quiz-instructions">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><ShieldAlert className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900">{quiz.title}</h1>
              <p className="text-sm text-slate-500">{quiz.questions.length} soal · {quiz.duration_minutes} menit · 1x kesempatan</p>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50 p-5">
            <p className="flex items-center gap-2 font-bold text-orange-700"><AlertTriangle className="h-5 w-5" /> Tata Tertib Pengerjaan Kuis</p>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
              <li>Layar akan berpindah ke <b>mode layar penuh (full screen)</b> selama kuis berlangsung.</li>
              <li>Anda <b>tidak diperkenankan</b> membuka tab, browser, atau aplikasi lain selama pengerjaan kuis.</li>
              <li>Batas pelanggaran hanya <b>1 (satu) kali</b>. Pelanggaran berikutnya akan membuat jawaban kuis <b>dikumpulkan otomatis</b> meskipun belum selesai.</li>
              <li>Menyalin teks, klik kanan, tangkapan layar, dan alat pengembang (DevTools) dinonaktifkan.</li>
            </ol>
          </div>

          <p className="mt-5 text-sm text-slate-500">Pastikan koneksi stabil dan Anda siap sebelum memulai. Timer berjalan setelah Anda menekan tombol di bawah.</p>
          <button data-testid="quiz-start-button" onClick={beginQuiz} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 transition">
            <Maximize className="h-4 w-4" /> Saya Mengerti, Mulai Kuis
          </button>
        </div>
      </div>
    );
  }

  const origQ = qOrder[current];
  const q = quiz.questions[origQ];
  const opts = optOrder[origQ] || q.options.map((_, i) => i);
  const answered = answers.filter((a) => a && a.length > 0).length;

  // ---------- QUIZ (running) ----------
  return (
    <div className="max-w-3xl mx-auto space-y-6 select-none" style={{ userSelect: "none" }}>
      {warnOverlay && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-rose-950/70 backdrop-blur-sm p-4" data-testid="quiz-warning-overlay">
          <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50"><ShieldAlert className="h-7 w-7 text-rose-600" /></div>
            <h3 className="mt-4 font-heading text-xl font-extrabold text-rose-700">Peringatan Pelanggaran!</h3>
            <p className="mt-2 text-sm text-slate-600">Anda terdeteksi keluar dari layar kuis (pindah tab / keluar layar penuh). Ini adalah peringatan pertama Anda.</p>
            <p className="mt-2 text-sm font-semibold text-rose-600">Pelanggaran berikutnya akan langsung mengumpulkan jawaban kuis Anda secara otomatis.</p>
            <button data-testid="quiz-resume-button" onClick={resumeFromWarning} className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">Kembali ke Kuis</button>
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{quiz.title}</h1>
          <div data-testid="quiz-timer" className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold font-mono ${timeLeft <= 30 ? "bg-rose-50 text-rose-600 animate-pulse" : "bg-slate-900 text-white"}`}>
            <Clock className="h-4 w-4" />
            {String(Math.floor((timeLeft || 0) / 60)).padStart(2, "0")}:{String((timeLeft || 0) % 60).padStart(2, "0")}
          </div>
        </div>
        <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600"><ShieldAlert className="h-3.5 w-3.5" /> Mode pengawasan aktif · jangan tinggalkan layar ini</p>
        <div className="mt-3 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${(answered / quiz.questions.length) * 100}%` }} />
        </div>
        <p className="mt-1 text-xs text-slate-400">{answered} dari {quiz.questions.length} soal terjawab</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {qOrder.map((origIdx, i) => (
          <button key={i} data-testid={`quiz-nav-${i}`} onClick={() => setCurrent(i)} className={`h-9 w-9 rounded-lg text-sm font-semibold transition ${
            i === current ? "bg-blue-600 text-white" : (answers[origIdx] && answers[origIdx].length > 0) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}>{i + 1}</button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Soal {current + 1}{q.multiple && <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] text-orange-600 normal-case">Pilih satu atau lebih</span>}</p>
        <p className="mt-2 text-lg font-semibold text-slate-900">{q.question}</p>
        <div className="mt-5 space-y-3">
          {opts.map((origOi, dispOi) => {
            const opt = q.options[origOi];
            const sel = (answers[origQ] || []).includes(origOi);
            return (
              <button key={origOi} data-testid={`quiz-option-button-${dispOi}`} onClick={() => select(origQ, origOi)} className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition ${
                sel ? "border-blue-500 bg-blue-50 text-blue-800 ring-2 ring-blue-500/20" : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}>
                <span className={`flex h-6 w-6 items-center justify-center text-xs font-bold ${q.multiple ? "rounded-md" : "rounded-full"} ${sel ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>{q.multiple ? (sel ? "✓" : "") : String.fromCharCode(65 + dispOi)}</span>
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button disabled={current === 0} onClick={() => setCurrent((c) => c - 1)} className="flex items-center gap-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Sebelumnya</button>
        {current < quiz.questions.length - 1 ? (
          <button onClick={() => setCurrent((c) => c + 1)} className="flex items-center gap-1 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Selanjutnya <ChevronRight className="h-4 w-4" /></button>
        ) : (
          <button data-testid="quiz-submit-button" onClick={() => submit()} disabled={submitting} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Kirim Jawaban
          </button>
        )}
      </div>
    </div>
  );
}
